import React from 'react'
import styled from 'styled-components'
import { brand, gray } from 'sutra/tokens/colors'

export interface PressableToggleButtonProps {
  pressed: boolean
  onPressedChange: (next: boolean) => void
  children: React.ReactNode
  disabled?: boolean
  style?: React.CSSProperties
  className?: string
  id?: string
}

export const PressableToggleButton: React.FC<PressableToggleButtonProps> = ({
  pressed,
  onPressedChange,
  children,
  disabled,
  style,
  className,
  id,
}) => (
  <StyledToggle
    id={id}
    type="button"
    aria-pressed={pressed}
    $pressed={pressed}
    disabled={disabled}
    onClick={() => !disabled && onPressedChange(!pressed)}
    style={style}
    className={className}
  >
    {children}
  </StyledToggle>
)

PressableToggleButton.displayName = 'PressableToggleButton'

const StyledToggle = styled.button<{ $pressed: boolean }>`
  height: 32px;
  padding: 0 12px;
  border: 0;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background 0.15s,
    color 0.15s;

  background: ${({ $pressed }) =>
    $pressed ? brand.stillNight[800] : gray[100]};
  color: ${({ $pressed }) => ($pressed ? brand.white : brand.stillNight[900])};

  &:hover:not(:disabled) {
    background: ${({ $pressed }) =>
      $pressed ? brand.stillNight[900] : gray[200]};
    color: ${({ $pressed }) =>
      $pressed ? brand.white : brand.stillNight[900]};
  }

  &:focus-visible {
    outline: 2px solid ${brand.stillNight[800]};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

// ---------------------------------------------------------------------------
// PressableToggleButtonGroup — single-select radio-style button group
// ---------------------------------------------------------------------------

export interface PressableToggleButtonGroupOption<T extends string> {
  label: React.ReactNode
  value: T
}

export interface PressableToggleButtonGroupProps<T extends string> {
  value: T
  onChange: (next: T) => void
  options: PressableToggleButtonGroupOption<T>[]
  disabled?: boolean
  size?: 'small' | 'default'
  block?: boolean
  className?: string
  style?: React.CSSProperties
}

export function PressableToggleButtonGroup<T extends string>({
  value,
  onChange,
  options,
  disabled,
  size = 'default',
  block = false,
  className,
  style,
}: PressableToggleButtonGroupProps<T>): React.ReactElement {
  return (
    <GroupRow
      role="radiogroup"
      className={className}
      style={style}
      $block={block}
    >
      {options.map((opt) => (
        <GroupButton
          key={opt.value}
          type="button"
          role="radio"
          aria-checked={opt.value === value}
          $pressed={opt.value === value}
          $size={size}
          $block={block}
          disabled={disabled}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </GroupButton>
      ))}
    </GroupRow>
  )
}

const GroupRow = styled.div<{ $block: boolean }>`
  display: ${({ $block }) => ($block ? 'flex' : 'inline-flex')};
  width: ${({ $block }) => ($block ? '100%' : 'auto')};
  gap: 4px;
`

const GroupButton = styled.button<{
  $pressed: boolean
  $size: 'small' | 'default'
  $block: boolean
}>`
  flex: ${({ $block }) => ($block ? '1 1 0' : 'none')};
  height: ${({ $size }) => ($size === 'small' ? '28px' : '32px')};
  padding: 0 12px;
  border: 0;
  border-radius: 6px;
  font-size: ${({ $size }) => ($size === 'small' ? '12px' : '13px')};
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background 0.15s,
    color 0.15s;

  background: ${({ $pressed }) =>
    $pressed ? brand.stillNight[800] : gray[100]};
  color: ${({ $pressed }) => ($pressed ? brand.white : brand.stillNight[900])};

  &:hover:not(:disabled) {
    background: ${({ $pressed }) =>
      $pressed ? brand.stillNight[900] : gray[200]};
    color: ${({ $pressed }) =>
      $pressed ? brand.white : brand.stillNight[900]};
  }

  &:focus-visible {
    outline: 2px solid ${brand.stillNight[800]};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`
